/** JSON client for the BuzzBee API (same origin; the session is an HttpOnly cookie). */

export type UpgradeInfo = {
  feature: string;
  label: string;
  plan: string;
  planName: string;
  product: 'buzzbee' | 'receptionistbee';
  productName: string;
  managedBy?: 'hive' | 'standalone';
  manageUrl?: string | null;
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly data: Record<string, unknown> = {},
  ) {
    super(message);
  }
  get upgrade(): UpgradeInfo | null {
    return (this.data.upgrade as UpgradeInfo | undefined) ?? null;
  }
}

const base = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

/** An API path as a URL for elements that fetch on their own (e.g. <audio src>), cookie-authenticated. */
export const apiUrl = (path: string) => `${base}${path}`;

export async function api<T = any>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    method: init.method ?? (init.body !== undefined ? 'POST' : 'GET'),
    credentials: 'include',
    headers: init.body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const text = await res.text();
  const json = text ? (JSON.parse(text) as Record<string, any>) : {};
  if (!res.ok) {
    const err = (json.error ?? {}) as Record<string, any>;
    throw new ApiError(res.status, err.code ?? String(res.status), err.message ?? (typeof json.error === 'string' ? json.error : 'Request failed'), err);
  }
  return json as T;
}

export const get = <T = any>(path: string) => api<T>(path);
export const post = <T = any>(path: string, body: unknown = {}) => api<T>(path, { method: 'POST', body });
export const put = <T = any>(path: string, body: unknown) => api<T>(path, { method: 'PUT', body });
export const patch = <T = any>(path: string, body: unknown) => api<T>(path, { method: 'PATCH', body });
export const del = <T = any>(path: string) => api<T>(path, { method: 'DELETE' });

export function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Something went wrong';
}

export function formatWhen(iso: string | number | Date, tz?: string): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(d);
}

export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds) return '0:00';
  return `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`;
}
